/**
 * HierarchyBreadcrumb Component
 *
 * Displays a clickable breadcrumb navigation trail for the SPLH Dashboard hierarchy.
 * Uses MUI Breadcrumbs component with custom styling.
 *
 * Features:
 * - Shows dynamic level names from hierarchyMetadata
 * - Clickable items for navigation to any level
 * - Home icon for root level
 * - Current level highlighted
 * - Responsive with overflow handling
 *
 * @module pages/common/dashboards/SPLHDashboard/components/HierarchyBreadcrumb
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Breadcrumbs,
  Link,
  Typography,
  Box,
} from '@mui/material';
import {
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';

/**
 * HierarchyBreadcrumb Component
 *
 * @param {Object} props - Component props
 * @param {Array} props.breadcrumb - Array of breadcrumb items from navigation hook
 * @param {Object} props.hierarchyMetadata - Metadata with level info
 * @param {string} [props.rootLabel] - Label for root level (default: client name or "All Locations")
 * @param {Function} props.onNavigate - Callback when a breadcrumb is clicked
 * @param {boolean} [props.showHome=true] - Whether to show home icon for root
 * @returns {React.Component} HierarchyBreadcrumb component
 */
const HierarchyBreadcrumb = ({
  breadcrumb = [],
  hierarchyMetadata,
  rootLabel,
  onNavigate,
  showHome = true,
}) => {
  // Determine the root label
  const displayRootLabel = rootLabel || hierarchyMetadata?.clientName || 'All Locations';

  // Handle breadcrumb click
  const handleClick = (index) => (event) => {
    event.preventDefault();
    if (onNavigate) {
      onNavigate(index);
    }
  };

  // If at root (no breadcrumb), show nothing
  if (!breadcrumb || breadcrumb.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
        aria-label="hierarchy navigation"
        sx={{
          '& .MuiBreadcrumbs-ol': {
            flexWrap: 'nowrap',
          },
          '& .MuiBreadcrumbs-li': {
            whiteSpace: 'nowrap',
          },
        }}
      >
        {/* Root/Home item */}
        <Link
          component="button"
          variant="body2"
          underline="hover"
          onClick={handleClick(-1)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'text.secondary',
            cursor: 'pointer',
            border: 'none',
            background: 'none',
            padding: 0,
            font: 'inherit',
            '&:hover': {
              color: '#7C3AED',
            },
          }}
        >
          {showHome && <HomeIcon sx={{ fontSize: 18 }} />}
          {displayRootLabel}
        </Link>

        {/* Intermediate breadcrumb items (all except last) */}
        {breadcrumb.slice(0, -1).map((item, index) => (
          <Link
            key={item.id || index}
            component="button"
            variant="body2"
            underline="hover"
            onClick={handleClick(index)}
            sx={{
              color: 'text.secondary',
              cursor: 'pointer',
              border: 'none',
              background: 'none',
              padding: 0,
              font: 'inherit',
              '&:hover': {
                color: '#7C3AED',
              },
            }}
          >
            {item.name}
          </Link>
        ))}

        {/* Current level (last item, not clickable) */}
        {breadcrumb.length > 0 && (
          <Typography
            variant="body2"
            color="text.primary"
            fontWeight={500}
          >
            {breadcrumb[breadcrumb.length - 1].name}
          </Typography>
        )}
      </Breadcrumbs>
    </Box>
  );
};

HierarchyBreadcrumb.propTypes = {
  /** Array of breadcrumb items from navigation hook */
  breadcrumb: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string.isRequired,
      level: PropTypes.string,
    })
  ),
  /** Metadata with level information */
  hierarchyMetadata: PropTypes.shape({
    levels: PropTypes.arrayOf(
      PropTypes.shape({
        level: PropTypes.string.isRequired,
        displayName: PropTypes.string.isRequired,
      })
    ),
    clientName: PropTypes.string,
  }),
  /** Label for root level */
  rootLabel: PropTypes.string,
  /** Callback when a breadcrumb is clicked (receives index, -1 for root) */
  onNavigate: PropTypes.func,
  /** Whether to show home icon for root */
  showHome: PropTypes.bool,
};

export default HierarchyBreadcrumb;
